import { getNumeralsMode } from "./numerals";

/**
 * The single Intl locale tag for the whole product.
 *
 * The app UI language is `"en"` / `"ar"` (see `SUPPORTED_LOCALES`), but the
 * Web `Intl` APIs (`Intl.NumberFormat`, `Intl.DateTimeFormat`,
 * `toLocaleDateString`, …) need a full BCP-47 tag. Screens used to each pick
 * their own — `ar-EG` (Egyptian month names يناير/فبراير) on some, `ar-JO`
 * (Levantine كانون الثاني/شباط) on others; `en-US` (month-first dates) vs
 * `en-GB` (day-first) — so the *same* app rendered different Arabic month
 * names and different date orderings on adjacent screens.
 *
 * This is the one place that maps the UI language to a canonical tag:
 *   - `"ar"` → `"ar-JO"` — Jordan market, Levantine month names.
 *   - anything else → `"en-GB"` — day-first dates, matches the Jordan locale.
 *
 * Pass the active language (`i18next.language` / `useTranslation().i18n.language`,
 * or `useIntlLocale()` for the ergonomic hook) — never hardcode a tag at a
 * call site again.
 *
 * Honours the per-user numerals preference: in Arabic, `"latin"` mode appends
 * the `-u-nu-latn` Unicode numbering extension (`ar-JO-u-nu-latn`) so digits
 * render Western while language, month names, and separators stay Arabic. The
 * default (`"arabic-indic"`) keeps plain `ar-JO`. English is unaffected.
 */
export function intlLocale(lang: string | null | undefined): string {
  // Match ANY Arabic tag, not just the bare "ar". i18next runs with
  // `nonExplicitSupportedLngs: true`, so the language detector can resolve a
  // regional tag like "ar-JO" / "ar-SA" from the browser. An exact `=== "ar"`
  // check would fall through to "en-GB" for those, handing ICU English plural
  // categories and Latin digits for Arabic copy. Normalize every ar-prefixed
  // tag to the Jordan base.
  if (lang === "ar" || lang?.startsWith("ar-"))
    return getNumeralsMode() === "latin" ? "ar-JO-u-nu-latn" : "ar-JO";
  return "en-GB";
}

/**
 * DATE-ONLY Intl locale tag (Iwan «يوليو» month policy).
 *
 * `intlLocale` above stays `ar-JO` — it drives numbers, currency, AND dates,
 * and `ar-JO` yields Levantine Gregorian month names (تموز / أيار / نيسان).
 * The founder-approved policy is that Gregorian month names should read in the
 * pan-Arab Egyptian style (يوليو / مايو / أبريل) instead, WITHOUT disturbing
 * number or currency formatting.
 *
 * `ar-EG` is the correct tag for that: verified via `Intl` it shares `ar-JO`'s
 * `firstDay: 6` / `weekend: [5,6]` and identical weekday names, and it keeps
 * Arabic-Indic digits (٠-٩) — the ONLY visible delta from `ar-JO` is the
 * Gregorian month names. `ar-u-ca-gregory` is WRONG here — it forces Latin
 * digits.
 *
 * Use this at DATE-FORMATTING call sites only (month / weekday text). Leave
 * `intlLocale` in place for `Intl.NumberFormat` / currency / any non-date use.
 * Hijri (`islamic-umalqura`) paths are separate and untouched.
 *
 * Honours the per-user numerals preference too: `"latin"` mode appends
 * `-u-nu-latn` (`ar-EG-u-nu-latn`) so the day/year numbers in a date read
 * Western while the Egyptian-style month names are preserved.
 */
export function intlDateLocale(lang: string | null | undefined): string {
  // Match ANY Arabic tag (see `intlLocale` — `nonExplicitSupportedLngs` can
  // surface "ar-JO" / "ar-SA"). Exact `=== "ar"` would send regional Arabic
  // through the English date path.
  if (lang === "ar" || lang?.startsWith("ar-"))
    return getNumeralsMode() === "latin" ? "ar-EG-u-nu-latn" : "ar-EG";
  return "en-GB";
}
